import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { listFolders, createFolder } from "@/lib/api";
import { toast } from "sonner";

export const PORTAL_OPTIONS = [
  { value: "parent", label: "Parent Portal" },
  { value: "student", label: "Student Portal" },
  { value: "faculty", label: "Faculty Portal" },
  { value: "alumni", label: "Alumni Portal" },
];

/** Shared builder fields: portals, notes, tags, folder. */
export default function PortalNotesFields({
  portals, setPortals, notes, setNotes,
  tags = [], setTags, folderId = null, setFolderId,
}) {
  const [folders, setFolders] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    listFolders().then(r => setFolders(r.folders || [])).catch(() => {});
  }, []);

  const toggle = (v) =>
    setPortals(portals.includes(v) ? portals.filter(x => x !== v) : [...portals, v]);

  const addTag = () => {
    const t = tagDraft.trim().toLowerCase();
    if (!t || tags.includes(t)) { setTagDraft(""); return; }
    setTags([...tags, t]);
    setTagDraft("");
  };
  const removeTag = (t) => setTags(tags.filter(x => x !== t));

  const addFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      const f = await createFolder({ name });
      setFolders([...folders, f]);
      setFolderId?.(f.id);
      setNewFolder("");
      toast.success(`Folder "${name}" created`);
    } catch { toast.error("Could not create folder"); }
    finally { setCreatingFolder(false); }
  };

  return (
    <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-6" data-testid="portal-notes-section">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Veracross Portals</div>
        <p className="text-xs text-zinc-500 mt-1">Which portals will show this component? Select one or more.</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {PORTAL_OPTIONS.map((p) => (
            <label
              key={p.value}
              htmlFor={`portal-${p.value}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-all duration-150 ${
                portals.includes(p.value)
                  ? "border-zinc-950 bg-zinc-50"
                  : "border-zinc-200 hover:border-zinc-300"
              }`}
              data-testid={`portal-option-${p.value}`}
            >
              <Checkbox
                id={`portal-${p.value}`}
                checked={portals.includes(p.value)}
                onCheckedChange={() => toggle(p.value)}
                data-testid={`portal-checkbox-${p.value}`}
              />
              <span className="text-sm font-medium text-zinc-950">{p.label}</span>
            </label>
          ))}
        </div>
      </div>

      {setFolderId && (
        <div>
          <Label className="text-xs text-zinc-500">Folder</Label>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => setFolderId(null)}
              data-testid="folder-option-none"
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                !folderId ? "bg-zinc-900 text-white border-zinc-900"
                          : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >No folder</button>
            {folders.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFolderId(f.id)}
                data-testid={`folder-option-${f.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  folderId === f.id ? "bg-zinc-900 text-white border-zinc-900"
                                     : "border-zinc-200 text-zinc-600 hover:border-zinc-300"
                }`}
              >{f.name}</button>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFolder(); } }}
              placeholder="New folder name"
              className="h-9 text-sm"
              data-testid="new-folder-input"
            />
            <button
              type="button"
              onClick={addFolder}
              disabled={creatingFolder || !newFolder.trim()}
              data-testid="new-folder-btn"
              className="h-9 px-3 rounded-md bg-zinc-100 hover:bg-zinc-200 text-xs font-medium disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5 inline -mt-0.5 mr-1" /> Add
            </button>
          </div>
        </div>
      )}

      {setTags && (
        <div>
          <Label className="text-xs text-zinc-500">Tags</Label>
          <div className="flex flex-wrap gap-1.5 mt-2 min-h-[28px]">
            {tags.map(t => (
              <Badge key={t} variant="secondary" className="gap-1 pr-1">
                {t}
                <button
                  type="button"
                  onClick={() => removeTag(t)}
                  data-testid={`tag-remove-${t}`}
                  className="hover:bg-zinc-200 rounded-full"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="Add a tag (press Enter)"
              className="h-9 text-sm"
              data-testid="tag-input"
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="notes" className="text-xs text-zinc-500">
          Notes / Purpose
        </Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal note. What is this component for? Who manages it?"
          className="mt-1.5 resize-none"
          rows={3}
          data-testid="notes-input"
        />
      </div>
    </section>
  );
}
